// Widget i18n dictionaries — lightweight, no dependency.
// 目标：覆盖访客侧能看见的所有 UI 字符串；服务端下发的 app 配置（welcome_message / agent.name 等）不在此范围。
//
// 约定：key 用"场景.含义"的形式；变量插值用 {name} 风格，交给 t() 做 replace。

export const locales = {
    zh: {
        // 通用
        'common.send': '发送',
        'common.submit': '提交',
        'common.cancel': '取消',
        'common.close': '关闭',
        'common.search': '搜索',
        'common.loading': '加载中...',
        'common.retry': '重试',

        // 标题 / 品牌
        'brand.default_title': '客服',
        'brand.default_subtitle': '在线客服',
        'brand.default_welcome': '你好，有什么可以帮到你的?',
        'brand.service_center': '客服中心',
        'brand.customer_service': "客服",
        'launcher.label_default': "有问题？和我们聊聊",
        'launcher.label_close': "隐藏此提示",
        'quick.group_label': "快捷操作",
        'quick.dismiss': "关闭快捷操作",
        'home.start_conversation': "发起对话",
        'status.business_hours': "服务时间：{hours}",
        'chat.conversation_fallback': "对话",

        // Floating button
        'fab.aria_open_chat': '打开客服对话',

        // 连线状态
        'status.connecting': '正在为你转接在线客服，可能需要2～3分钟后回复',
        'status.offline_queued': '看起来暂无在线客服，问题我们已经记录，并将在上线后第一时间处理',
        'status.agents_online': '{count} 位客服在线',
        'status.agents_overflow': '还有 {count} 位客服',
        'status.offline': '离线',
        'status.recent_chat': '最近：{text}',
        'status.connection_lost': '连接已断开，正在重新连接…',

        // 初始化失败错误页（/v1/chat 拿不到有效会话——app_id 无效/缺失、连接层
        // 失败、服务端 5xx 三类，见 widget.js renderInitError）
        'error.app_unavailable_title': '聊天暂时不可用',
        'error.app_unavailable_body': '当前无法加载聊天，请稍后再试。',
        'error.network_title': '连接出了点问题',
        'error.network_body': '暂时无法连接，请检查网络后重试。',
        'error.server_title': '出了点问题',
        'error.server_body': '服务暂时不稳定，请稍后重试。',

        // 头部工具（语言切换 / 设置弹层）
        'header.language': '语言',
        'header.settings': '设置',
        'settings.sound': '新消息提示音',
        'settings.sound_hint': '面板关闭时收到回复响一声',

        // 导航 / tabs
        'nav.home': '首页',
        'nav.chat_list': '会话列表',
        'nav.chat_tab': '聊天',
        'nav.knowledge_tab': '知识',
        'nav.knowledge_base': '知识库',
        'nav.game_center': '游戏中心',

        // 输入区
        'input.placeholder': '输入消息...',
        'input.attach_image': '发送图片',
        'input.record_voice': '语音输入',
        'input.send': '发送',

        // 聊天 frame
        'chat.loading_frame': '正在加载聊天界面...',
        'chat.loading_history': '正在加载历史消息',
        'chat.load_more_history': '向上滚动加载更多消息',
        'chat.close_session': '关闭会话',
        'chat.scroll_left': '向左滚动',
        'chat.scroll_right': '向右滚动',
        'chat.call_menu': '通话',
        'chat.voice_call': '语音通话',
        'chat.video_call': '视频通话',
        'chat.screen_share': '屏幕共享',
        'chat.reply_to': '回复',
        'chat.yesterday': '昨天',
        'chat.reply_self': '你',
        'chat.reply_agent': '客服',
        'chat.days_ago': '{days}天前',
        'chat.back_to_chat': '返回聊天',
        'chat.new_chat': '+ 新会话',
        'chat.no_chats': '暂无会话记录',
        'chat.no_messages': '暂无消息',
        'chat.chat_number': '会话 #{id}',
        'chat.typing': '客服正在输入',
        'chat.new_msg_one': '1 条新消息',
        'chat.new_msgs': '{count} 条新消息',
        'chat.jump_to_latest': '滚动到最新消息',
        'chat.reply_self': '自己',
        'chat.action_copy': '复制',
        'chat.copied': '已复制',
        'chat.copy_failed': '复制失败',
        'chat.action_reply': '回复',
        'chat.reply_agent': '客服',
        'chat.powered_by': 'Powered by',
        'chat.reply_image_placeholder': '图片',
        'chat.banned_notice': '你的发送权限已被限制，消息将无法送达',
        'chat.banned_placeholder': '发送权限已被限制',
        'chat.email_gate_notice': '请先留下邮箱以继续对话',
        'chat.email_gate_placeholder': '请先留下邮箱',
        'chat.image_invalid_type': '请上传图片文件',
        'chat.image_too_large': '图片太大，请选择 10MB 以内的图片',
        'chat.image_upload_failed': '图片发送失败，请重试',

        // Home view
        'home.send_message': '发送消息',
        'home.start_new': '开始新对话',
        'home.search_articles': '搜索帮助文章...',
        'home.view_all_chats': '查看全部对话',
        'home.search_results': '搜索结果',
        'home.help_articles': '帮助文章',
        'home.no_articles': '未找到相关文章',

        // Message area
        'msg.notifications_title': '我们回来时通知你',
        'msg.notifications_cta': '有回复时通知你',
        'msg.references': '参考来源',
        'msg.email_placeholder': '请输入您的邮箱',
        'msg.submitted_thanks': '已提交，感谢！',
        'msg.select_placeholder': '请选择…',
        'msg.form_required': '请填写必填项',
        'msg.form_submit_failed': '提交失败，请重试',
        'msg.email_submitted': '邮箱已提交',
        'msg.leave_email': '留下邮箱',
        'msg.wait_guide': '客服引导',
        'msg.bind_push': '绑定消息推送',
        'msg.system_tip': '系统提示',
        'msg.browser_push': '浏览器推送',
        'msg.browser_push_desc': '关掉这个页面也能收到',
        'msg.tg_push': 'Telegram',
        'msg.tg_push_desc': '发到你的 Telegram',
        'msg.email_push': '邮件',
        'msg.email_push_desc': '发到你的收件箱',
        'msg.notifications_multi_hint': '可以同时开启多种。',
        'msg.notif_on': '已开启',
        'msg.notif_working': '处理中…',
        'msg.notif_pending': '待确认',
        'msg.notif_blocked': '已被拦截',
        'msg.notif_unavailable': '不可用',
        'msg.notif_push_no_browser': '这个浏览器不支持推送通知。',
        'msg.notif_push_no_site': '这个站点还没接好浏览器推送。',
        'msg.notif_push_use_other': '改用 {channels} 一样能找到你。',
        'msg.notif_failed': '没成功，再试一次。',
        'msg.notif_turned_off': '已关闭',
        'msg.notif_blocked_help': '这个站点的通知被浏览器拦截了。到浏览器设置里放行，然后再试一次。',
        'msg.notif_tg_pending_hint': '打开 Telegram 点 Start，连上后这张卡会自己更新。',
        'msg.notif_tg_on_detail': '已连接 Telegram。',
        'msg.notif_email_on_detail': '回复会发到 {email}。',
        'msg.notifications_pick_multi': '选择我们联系你的方式：',
        'msg.notifications_pick_single': '选一种联系你的方式：',
        'msg.sending': '发送中',
        'msg.send_failed': '发送失败，点击重试',
        'msg.updateAppHint': '请更新 App 以查看完整内容',
        'msg.unsupported': '这条消息暂时无法显示，请更新 App 后查看。',

        // GIF
        'gif.search': '搜索 GIF',
        'gif.sending': 'GIF发送中...',
        'gif.empty': '没有找到 GIF',
        'gif.failed': 'GIF 加载失败',

        // 绑定提示
        'binding.telegram_hint': '点击下方链接在Telegram中与客服沟通，即使离开网页也能收到回复。',
        'binding.telegram_failed': '生成Telegram连接失败，请稍后再试',
        'binding.generate_failed': '生成绑定码失败，请稍后再试',
        'binding.copied': '绑定命令已复制到剪贴板',
        'binding.push_not_ready': '推送功能未初始化',
        'binding.push_enabled_msg': '浏览器推送已开启，关掉这个页面也能收到回复。',
        'binding.push_disabled_msg': '浏览器推送已关闭。',
        'binding.push_request_msg': '开启浏览器推送，关掉页面后也能收到回复。',
        'binding.push_unavailable': '这个浏览器暂时用不了推送。',
        'binding.push_denied_status_message': '推送通知权限已被拒绝，请在浏览器设置中手动启用',
        'binding.push_enable_failed': '启用推送通知失败：{error}',
        'binding.push_disable_failed': '取消推送通知失败：{error}',
        'binding.permission_denied_intro': '已拒绝了通知权限。要重新启用推送通知，请按以下步骤操作：\n\n',
        'binding.permission_chrome': 'Chrome 浏览器：\n1. 点击地址栏左侧的锁形图标\n2. 找到"通知"选项\n3. 将其改为"允许"\n4. 刷新页面后重试',
        'binding.permission_firefox': 'Firefox 浏览器：\n1. 点击地址栏左侧的锁形图标\n2. 点击"连接安全" > "更多信息"\n3. 选择"权限"标签\n4. 找到"接收通知"，取消勾选"使用默认"并选择"允许"\n5. 刷新页面后重试',
        'binding.permission_safari': 'Safari 浏览器：\n1. 点击 Safari 菜单 > 偏好设置\n2. 选择"网站"标签\n3. 在左侧选择"通知"\n4. 找到本网站，将权限改为"允许"\n5. 刷新页面后重试',
        'binding.permission_edge': 'Edge 浏览器：\n1. 点击地址栏左侧的锁形图标\n2. 点击"网站权限"\n3. 找到"通知"选项\n4. 将其改为"允许"\n5. 刷新页面后重试',
        'binding.permission_generic': '1. 在浏览器设置中找到"网站权限"或"隐私设置"\n2. 找到本网站的通知权限\n3. 将权限改为"允许"\n4. 刷新页面后重试',
        'binding.title_telegram': 'Telegram 绑定',
        'binding.title_push': '推送通知',
        'binding.title_email': '邮箱绑定',
        'binding.open_telegram': '打开 Telegram',
        'binding.copy_command': '复制绑定命令',
        'binding.enable_push_btn': '启用推送通知',
        'binding.disable_push_btn': '关闭推送通知',
        'binding.view_permission_help': '查看如何启用权限',
        'binding.status_enabled': '✓ 已启用',
        'binding.status_pending': '⏳ 等待授权',
        'binding.status_off': '✗ 已关闭',
        'binding.status_denied': '⚠️ 权限被拒绝',
        'binding.status_waiting': '⏳ 等待绑定',

        // 通话
        'call.end': '结束通话',
        'call.mute': '静音',
        'call.unmute': '取消静音',
        'call.camera_off': '关闭摄像头',
        'call.camera_on': '开启摄像头',
        'call.accept': '接听',
        'call.reject': '拒绝',
        'call.connecting': '正在连接...',
        'call.connected': '对方已接听',
        'call.declined': '对方拒绝了通话',
        'call.ended': '通话已结束',
        'call.cancelled': '对方已取消呼叫',
        'call.session_ended': '会话已结束',
        'chat.closed_notice': '这次会话已结束',
        'call.rate_prompt': '请为本次服务评分',
        'call.type_call': '通话',
        'call.type_screen_request': '查看您的屏幕',
        'call.invite': '{name} 邀请您进行{type}',
        'call.initializing': '正在初始化...',
        'call.initializing_call': '正在初始化通话...',
        'call.waiting_ellipsis': '等待对方接听...',
        'call.waiting': '等待对方接听',
        'call.accepted_connecting': '对方已接听，接通中…',
        'call.in_progress': '通话中 {time}',
        'call.connect_failed': '连接失败',
        'call.error_service_unreachable': '无法连接到通话服务，请检查网络后重试。',
        'call.failed_title': '通话失败',
        'call.view_other_screen': '查看对方屏幕',
        'call.screen_request_sending': '请求查看屏幕',
        'call.error_mic': '无法访问麦克风，请检查浏览器权限设置后重试',
        'call.error_camera': '无法访问摄像头，请检查浏览器权限设置后重试',
        'call.error_device_generic': '无法访问该设备，请检查浏览器权限设置后重试',
        'permission.mic_required': '需要麦克风权限',
        'permission.camera_required': '需要摄像头权限',
        'permission.open_settings': '去设置',
        'voice.record': '语音消息',
        'voice.cancel': '取消',
        'voice.stop': '完成',
        'voice.discard': '删除',
        'voice.send': '发送',
        'voice.unsupported': '你的浏览器不支持录音，请使用最新版 Chrome、Firefox、Edge 或 Safari',
        'voice.upload_failed': '语音发送失败，请重试',
        'call.error_screenshare_mobile': '移动设备暂不支持屏幕共享，请使用桌面浏览器',
        'call.error_screenshare_denied': '需要屏幕共享权限，请允许后重试',
        'call.error_screenshare_unsupported': '你的浏览器不支持屏幕共享，请使用最新版 Chrome、Firefox 或 Edge',
        'call.error_screenshare_start_failed': '无法启动屏幕共享，请确认已授予权限',
        'call.error_missing_token': '通话授权缺失，请关闭后重试',
        'call.error_not_ready': '正在初始化，请稍后再试',
        'call.error_start_audio_failed': '无法发起语音通话，请稍后再试',
        'call.error_start_video_failed': '无法发起视频通话，请稍后再试',
        'call.error_start_screenshare_failed': '无法发起屏幕共享，请稍后再试',
        'call.error_screenshare_unsupported_incoming': '您的浏览器不支持屏幕共享功能，无法接受此请求。\n\n请使用最新版本的Chrome、Firefox或Edge浏览器。',
        'call.error_screenshare_mobile_incoming': '移动设备不支持屏幕共享功能，无法接受此请求。\n\n请使用桌面浏览器。',

        // 邮箱绑定
        'email.bind_title': '邮箱推送绑定',
        'email.input_email': '请输入邮箱地址',
        'email.input_code': '请输入6位验证码',
        'email.send_code': '发送验证码',
        'email.verify': '验证',
        'email.verify_cta': '验证邮箱',
        'email.code_sent_hint': '验证码已发送到邮箱，5分钟内有效',
        'email.bound_success': '邮箱已绑定成功！',
        'email.bound': '已绑定',
        'email.resend_in': '{seconds}秒后重新发送',
        'email.sending': '发送中...',
        'email.verifying': '验证中...',
        'email.bound_message': '邮箱 {email} 绑定成功！将在收到新消息时接收邮件通知。',
        'email.confirm_known_hint': '我们会把回复通知到 {email}',
        'email.confirm_known_cta': '就用这个邮箱',
        'email.use_other': '用其他邮箱',
        'email.error_enter_address': '请输入邮箱地址',
        'email.error_invalid_address': '请输入有效的邮箱地址',
        'email.error_send_failed': '发送失败，请重试',
        'email.error_send_network': '发送失败，请检查网络连接',
        'email.error_code_length': '请输入6位验证码',
        'email.error_verify_failed': '验证失败，请重试',
        'email.error_verify_network': '验证失败，请检查网络连接',

        // 评分
        'rating.feedback_placeholder': '请输入您的反馈（可选）...',
        'rating.thank_you': '感谢你的反馈',
        'rating.service_review': '服务评价',
        'rating.thank_you_alt': '感谢您的评价！',
        'rating.prompt_default': '请为本次服务评分',
        'rating.submitting': '提交中...',
        'rating.submit_review': '提交评价',
        'rating.error_missing_chat': '会话信息缺失，无法提交',
        'rating.error_submit_failed': '提交失败，请稍后重试',
        'rating.error_network': '网络错误，请重试',

        // 已读
        'read.seen': '已读',
        'read.delivered': '已送达',

        // 游戏
        'game.start': '🎮 开始游戏',
        'game.tap_hint': '点击按钮在新窗口中开始游戏',

        // 知识库（独立页面 KnowledgeList/KnowledgeDetail/know-frame）
        'know.all_categories': '全部',
        'know.all_articles': '全部文章',
        'know.article_count': '{count} 篇文章',
        'know.article_count_one': '{count} 篇文章',
        'know.empty_kb': '暂无知识库文章',
        'know.view_article': '查看 →',
        'know.article_not_found': '文章未找到',
        'know.untitled': '无标题',
        'know.back': '返回',
        'know.published_on': '发布于 {date}',
        'know.loading_kb': '正在加载知识库...',
        'know.open_external': '在新窗口打开',

        // 嵌入式内容(embed)容器层文案
        'embed.completed': '已完成',
        'embed.content_label': '互动内容',
        'embed.load_failed': '这块内容没能加载出来',
    },

    en: {
        'common.send': 'Send',
        'common.submit': 'Submit',
        'common.cancel': 'Cancel',
        'common.close': 'Close',
        'common.search': 'Search',
        'common.loading': 'Loading...',
        'common.retry': 'Retry',

        'brand.default_title': 'Support',
        'brand.default_subtitle': 'Live support',
        'brand.default_welcome': 'Hi there! How can we help?',
        'brand.service_center': 'Help Center',
        'brand.customer_service': "Customer Service",
        'launcher.label_default': "Questions? Chat with us",
        'launcher.label_close': "Hide this message",
        'quick.group_label': "Quick actions",
        'quick.dismiss': "Dismiss quick actions",
        'home.start_conversation': "Start a conversation",
        'status.business_hours': "Hours: {hours}",
        'chat.conversation_fallback': "Conversation",

        'fab.aria_open_chat': 'Open support chat',

        'status.connecting': "Connecting you to an agent — someone will reply in a few minutes.",
        'status.offline_queued': "All agents are offline right now. We saved your message and will get back to you as soon as we're on.",
        'status.agents_online': '{count} agent(s) online',
        'status.agents_overflow': '{count} more agent(s)',
        'status.offline': 'Offline',
        'status.recent_chat': 'Recent: {text}',
        'status.connection_lost': 'Connection lost — reconnecting…',

        // Init-failure error page (/v1/chat couldn't return a valid session —
        // invalid/missing app_id, connection-layer failure, or server 5xx; see
        // widget.js renderInitError)
        'error.app_unavailable_title': 'Chat is unavailable',
        'error.app_unavailable_body': "We can't load chat right now. Please try again later.",
        'error.network_title': 'Connection problem',
        'error.network_body': "We couldn't connect. Check your connection and try again.",
        'error.server_title': 'Something went wrong',
        'error.server_body': "We're having trouble on our end. Please try again in a moment.",

        'header.language': 'Language',
        'header.settings': 'Settings',
        'settings.sound': 'Message sound',
        'settings.sound_hint': 'Chime when a reply arrives while closed',

        'nav.home': 'Home',
        'nav.chat_list': 'Chats',
        'nav.chat_tab': 'Chat',
        'nav.knowledge_tab': 'Help',
        'nav.knowledge_base': 'Help Center',
        'nav.game_center': 'Games',

        'input.placeholder': 'Type a message...',
        'input.attach_image': 'Attach image',
        'input.record_voice': 'Voice message',
        'input.send': 'Send',

        'chat.loading_frame': 'Loading chat...',
        'chat.loading_history': 'Loading earlier messages',
        'chat.load_more_history': 'Scroll up to load more',
        'chat.close_session': 'End chat',
        'chat.scroll_left': 'Scroll left',
        'chat.scroll_right': 'Scroll right',
        'chat.call_menu': 'Call',
        'chat.voice_call': 'Voice call',
        'chat.video_call': 'Video call',
        'chat.screen_share': 'Share screen',
        'chat.reply_to': 'Reply',
        'chat.yesterday': 'Yesterday',
        'chat.reply_self': 'You',
        'chat.reply_agent': 'Agent',
        'chat.days_ago': '{days}d ago',
        'chat.back_to_chat': 'Back to chat',
        'chat.new_chat': '+ New chat',
        'chat.no_chats': 'No conversations yet',
        'chat.no_messages': 'No messages',
        'chat.chat_number': 'Chat #{id}',
        'chat.typing': 'Agent is typing',
        'chat.new_msg_one': '1 new message',
        'chat.new_msgs': '{count} new messages',
        'chat.jump_to_latest': 'Jump to latest messages',
        'chat.reply_self': 'You',
        'chat.action_copy': 'Copy',
        'chat.copied': 'Copied',
        'chat.copy_failed': "Couldn't copy",
        'chat.action_reply': 'Reply',
        'chat.reply_agent': 'Agent',
        'chat.powered_by': 'Powered by',
        'chat.reply_image_placeholder': 'Image',
        'chat.banned_notice': 'Your access has been restricted — messages will not be delivered.',
        'chat.banned_placeholder': 'Messaging restricted',
        'chat.email_gate_notice': 'Please leave your email to continue the conversation.',
        'chat.email_gate_placeholder': 'Leave your email to continue',
        'chat.image_invalid_type': 'Please choose an image file',
        'chat.image_too_large': "That image is too large — please pick one under 10MB",
        'chat.image_upload_failed': "Couldn't send that image — please try again",

        'home.send_message': 'Send us a message',
        'home.start_new': 'Start a new conversation',
        'home.search_articles': 'Search help articles...',
        'home.view_all_chats': 'View all conversations',
        'home.search_results': 'Search results',
        'home.help_articles': 'Help articles',
        'home.no_articles': 'No articles found',

        'msg.notifications_title': 'Get notified when we\'re back',
        'msg.notifications_cta': 'Get notified when we reply',
        'msg.references': 'References',
        'msg.email_placeholder': 'Enter your email',
        'msg.submitted_thanks': 'Submitted, thanks!',
        'msg.select_placeholder': 'Select…',
        'msg.form_required': 'Please fill in the required fields',
        'msg.form_submit_failed': 'Submit failed — please try again',
        'msg.email_submitted': 'Email submitted',
        'msg.leave_email': 'Leave your email',
        'msg.wait_guide': 'Agent guide',
        'msg.bind_push': 'Enable push notifications',
        'msg.system_tip': 'System notice',
        'msg.browser_push': 'Browser push',
        'msg.browser_push_desc': 'Works even when this page is closed',
        'msg.tg_push': 'Telegram',
        'msg.tg_push_desc': 'Delivered to your Telegram',
        'msg.email_push': 'Email',
        'msg.email_push_desc': 'Sent to your inbox',
        'msg.notifications_multi_hint': 'You can turn on more than one.',
        'msg.notif_on': 'On',
        'msg.notif_working': 'Working…',
        'msg.notif_pending': 'Pending',
        'msg.notif_blocked': 'Blocked',
        'msg.notif_unavailable': 'Unavailable',
        'msg.notif_push_no_browser': 'This browser doesn\'t support push notifications.',
        'msg.notif_push_no_site': 'Browser push isn\'t set up on this site yet.',
        'msg.notif_push_use_other': 'Use {channels} instead — those reach you either way.',
        'msg.notif_failed': 'That didn\'t work — try again.',
        'msg.notif_turned_off': 'Turned off',
        'msg.notif_blocked_help': 'Notifications are blocked for this site. Allow them in your browser settings, then try again.',
        'msg.notif_tg_pending_hint': 'Open Telegram and press Start. This card updates as soon as you\'re connected.',
        'msg.notif_tg_on_detail': 'Connected to Telegram.',
        'msg.notif_email_on_detail': 'Replies go to {email}.',
        'msg.notifications_pick_multi': 'Choose where we should reach you:',
        'msg.notifications_pick_single': 'Choose one way to reach you:',
        'msg.sending': 'Sending…',
        'msg.send_failed': 'Failed to send — tap to retry',
        'msg.updateAppHint': 'Update the app to view the full content',
        'msg.unsupported': 'This message can\'t be displayed — update the app to view it.',

        'gif.search': 'Search GIFs',
        'gif.sending': 'Sending GIF...',
        'gif.empty': 'No GIFs found',
        'gif.failed': "Couldn't load GIFs",

        'binding.telegram_hint': 'Open the link below to chat with an agent via Telegram — replies still reach you after you leave the page.',
        'binding.telegram_failed': 'Could not create Telegram link, please try again',
        'binding.generate_failed': 'Could not create binding code, please try again',
        'binding.copied': 'Binding command copied to clipboard',
        'binding.push_not_ready': "Push notifications aren't ready yet",
        'binding.push_enabled_msg': 'Browser push is on — replies reach you even with this page closed.',
        'binding.push_disabled_msg': 'Browser push is off.',
        'binding.push_request_msg': 'Turn on browser push and replies reach you even after you close this page.',
        'binding.push_unavailable': 'Browser push isn\'t available here.',
        'binding.push_denied_status_message': 'Notification permission was denied — enable it manually in your browser settings',
        'binding.push_enable_failed': "Couldn't enable push notifications: {error}",
        'binding.push_disable_failed': "Couldn't turn off push notifications: {error}",
        'binding.permission_denied_intro': "Notifications are blocked. To turn them back on:\n\n",
        'binding.permission_chrome': "Chrome:\n1. Click the lock icon in the address bar\n2. Find \"Notifications\"\n3. Set it to \"Allow\"\n4. Refresh the page and try again",
        'binding.permission_firefox': "Firefox:\n1. Click the lock icon in the address bar\n2. Click \"Connection secure\" > \"More information\"\n3. Open the \"Permissions\" tab\n4. Find \"Receive Notifications\", uncheck \"Use Default\" and choose \"Allow\"\n5. Refresh the page and try again",
        'binding.permission_safari': "Safari:\n1. Open Safari > Preferences\n2. Go to the \"Websites\" tab\n3. Select \"Notifications\" on the left\n4. Find this site and set it to \"Allow\"\n5. Refresh the page and try again",
        'binding.permission_edge': "Edge:\n1. Click the lock icon in the address bar\n2. Click \"Permissions for this site\"\n3. Find \"Notifications\"\n4. Set it to \"Allow\"\n5. Refresh the page and try again",
        'binding.permission_generic': "1. Open your browser's site settings or privacy settings\n2. Find this site's notification permission\n3. Set it to \"Allow\"\n4. Refresh the page and try again",
        'binding.title_telegram': 'Telegram',
        'binding.title_push': 'Push notifications',
        'binding.title_email': 'Email',
        'binding.open_telegram': 'Open Telegram',
        'binding.copy_command': 'Copy command',
        'binding.enable_push_btn': 'Enable push notifications',
        'binding.disable_push_btn': 'Turn off push notifications',
        'binding.view_permission_help': 'See how to enable it',
        'binding.status_enabled': '✓ Enabled',
        'binding.status_pending': '⏳ Waiting for approval',
        'binding.status_off': '✗ Turned off',
        'binding.status_denied': '⚠️ Permission denied',
        'binding.status_waiting': '⏳ Waiting to link',

        'call.end': 'End call',
        'call.mute': 'Mute',
        'call.unmute': 'Unmute',
        'call.camera_off': 'Camera off',
        'call.camera_on': 'Camera on',
        'call.accept': 'Answer',
        'call.reject': 'Decline',
        'call.connecting': 'Connecting…',
        'call.connected': 'Call connected',
        'call.declined': 'Call declined',
        'call.ended': 'Call ended',
        'call.cancelled': 'Call cancelled',
        'call.session_ended': 'Chat session ended',
        'chat.closed_notice': 'This conversation has ended',
        'call.rate_prompt': 'Please rate this conversation',
        'call.type_call': 'call',
        'call.type_screen_request': 'screen view request',
        'call.invite': '{name} invites you to a {type}',
        'call.initializing': 'Initializing...',
        'call.initializing_call': 'Initializing call...',
        'call.waiting_ellipsis': 'Waiting for answer...',
        'call.waiting': 'Waiting for answer',
        'call.accepted_connecting': 'Answered, connecting…',
        'call.in_progress': 'On call {time}',
        'call.connect_failed': 'Connection failed',
        'call.error_service_unreachable': "Can't reach the call service. Check your network and try again.",
        'call.failed_title': 'Call failed',
        'call.view_other_screen': "Viewing their screen",
        'call.screen_request_sending': 'Requesting screen view',
        'call.error_mic': "We couldn't access your microphone. Check your browser's permission settings and try again.",
        'call.error_camera': "We couldn't access your camera. Check your browser's permission settings and try again.",
        'call.error_device_generic': "We couldn't access that device. Check your browser's permission settings and try again.",
        'permission.mic_required': 'Microphone access needed',
        'permission.camera_required': 'Camera access needed',
        'permission.open_settings': 'Open Settings',
        'voice.record': 'Voice message',
        'voice.cancel': 'Cancel',
        'voice.stop': 'Done',
        'voice.discard': 'Delete',
        'voice.send': 'Send',
        'voice.unsupported': "Your browser doesn't support voice recording. Try the latest Chrome, Firefox, Edge, or Safari.",
        'voice.upload_failed': "Couldn't send the voice message — please try again",
        'call.error_screenshare_mobile': "Screen sharing isn't supported on mobile — try a desktop browser instead.",
        'call.error_screenshare_denied': 'We need permission to share your screen. Please allow it and try again.',
        'call.error_screenshare_unsupported': "Your browser doesn't support screen sharing. Try the latest Chrome, Firefox, or Edge.",
        'call.error_screenshare_start_failed': "Couldn't start screen sharing — please make sure you allowed the permission.",
        'call.error_missing_token': 'Missing call authorization. Please close this and try again.',
        'call.error_not_ready': 'Still getting ready — please try again in a moment',
        'call.error_start_audio_failed': "Couldn't start the voice call — please try again",
        'call.error_start_video_failed': "Couldn't start the video call — please try again",
        'call.error_start_screenshare_failed': "Couldn't start screen sharing — please try again",
        'call.error_screenshare_unsupported_incoming': "Your browser doesn't support screen sharing, so this request can't be accepted.\n\nTry the latest Chrome, Firefox, or Edge.",
        'call.error_screenshare_mobile_incoming': "Screen sharing isn't supported on mobile, so this request can't be accepted.\n\nPlease use a desktop browser.",

        'email.bind_title': 'Email notifications',
        'email.input_email': 'Enter your email',
        'email.input_code': 'Enter the 6-digit code',
        'email.send_code': 'Send code',
        'email.verify': 'Verify',
        'email.verify_cta': 'Verify email',
        'email.code_sent_hint': 'Code sent to your email, valid for 5 minutes',
        'email.bound_success': 'Email linked successfully!',
        'email.bound': 'Linked',
        'email.resend_in': 'Resend in {seconds}s',
        'email.sending': 'Sending...',
        'email.verifying': 'Verifying...',
        'email.bound_message': 'Email {email} linked! You will receive email notifications for new messages.',
        'email.confirm_known_hint': "We'll send reply notifications to {email}",
        'email.confirm_known_cta': 'Use this email',
        'email.use_other': 'Use a different email',
        'email.error_enter_address': 'Please enter your email address',
        'email.error_invalid_address': 'Please enter a valid email address',
        'email.error_send_failed': 'Send failed, please try again',
        'email.error_send_network': 'Send failed, please check your connection',
        'email.error_code_length': 'Please enter the 6-digit code',
        'email.error_verify_failed': 'Verification failed, please try again',
        'email.error_verify_network': 'Verification failed, please check your connection',

        'rating.feedback_placeholder': 'Share your feedback (optional)...',
        'rating.thank_you': 'Thanks for your feedback',
        'rating.service_review': 'Service rating',
        'rating.thank_you_alt': 'Thanks for your rating!',
        'rating.prompt_default': 'Please rate this conversation',
        'rating.submitting': 'Submitting...',
        'rating.submit_review': 'Submit rating',
        'rating.error_missing_chat': "Missing chat info — can't submit",
        'rating.error_submit_failed': 'Submit failed — please try again shortly',
        'rating.error_network': 'Network error — please try again',

        'read.seen': 'Seen',
        'read.delivered': 'Delivered',

        'game.start': '🎮 Start game',
        'game.tap_hint': 'Tap the button to start the game in a new window',

        'know.all_categories': 'All',
        'know.all_articles': 'All articles',
        'know.article_count': '{count} articles',
        'know.article_count_one': '{count} article',
        'know.empty_kb': 'No articles in the help center yet',
        'know.view_article': 'View →',
        'know.article_not_found': 'Article not found',
        'know.untitled': 'Untitled',
        'know.back': 'Back',
        'know.published_on': 'Published {date}',
        'know.loading_kb': 'Loading the help center...',
        'know.open_external': 'Open in new window',

        // Embedded content (embed) container chrome
        'embed.completed': 'Done',
        'embed.content_label': 'Interactive content',
        'embed.load_failed': 'This content didn’t load.',
    },
};

// 语言集与落地站 index/lang/ 对齐（en zh_CN ru tr es de fr it pt id hi ja ko th），
// widget 侧统一用短码。数组顺序 = 语言弹层两列网格的显示顺序（按行填充）：
// 拉丁欧语 → 斯拉夫/土耳其 → CJK → 东南亚/南亚。
export const SUPPORTED_LANGS = ['en', 'zh', 'es', 'pt', 'fr', 'de', 'it', 'ru', 'tr', 'ja', 'ko', 'th', 'id', 'hi'];

// 语言自称（native name）—— 语言选择器只显示自称，不显示"German"这种他称，
// 因为看不懂当前界面语言的访客正是要靠自称找到自己的语言。
// pt 指向 pt-BR（面向巴西，与落地站 og:locale 的 pt_BR 一致）。
export const LANG_NATIVE_NAMES = {
    en: 'English',
    zh: '中文',
    es: 'Español',
    pt: 'Português',
    fr: 'Français',
    de: 'Deutsch',
    it: 'Italiano',
    ru: 'Русский',
    tr: 'Türkçe',
    ja: '日本語',
    ko: '한국어',
    th: 'ไทย',
    id: 'Indonesia',
    hi: 'हिन्दी',
};
